import Phaser from "phaser";
import type { SaveAdapter } from "@yjsgames/save";
import { BootScene } from "../scenes/BootScene.js";
import { GameScene } from "../scenes/GameScene.js";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import type { FeatureFlags } from "../env/featureFlags.js";
import { PALETTE } from "../themes/palette.js";
import { DESIGN } from "../data/balance.js";
import type { SafeAreaSource } from "@yjsgames/safe-area";
import { buildFastRenderConfig, buildFpsConfig } from "@yjsgames/perf";
import type { AccountLinkProvider } from "@yjsgames/account-link";
import type { SupportShareProvider } from "@yjsgames/support-contact";
import type { CrashlyticsPort } from "@yjsgames/crashlytics-ports";
import {
  CHEAT_ENABLED,
  FORCE_FAST_RENDER_ENABLED,
  FORCE_RAF_LOOP_ENABLED,
  FORCE_RENDER_RESOLUTION,
  FORCE_SMOOTH_STEP,
} from "../env/appFlavor.js";

declare global {
  interface Window {
    __moduleBrickGame?: Phaser.Game;
  }
}

/** 디자인 해상도 — 모바일 세로형. FIT + CENTER_BOTH로 PC에서도 중앙 정렬 세로 영역. */
export const DESIGN_WIDTH = DESIGN.width;
export const DESIGN_HEIGHT = DESIGN.height;

/**
 * Phaser registry로 주입하는 외부 서비스 묶음 — "로컬이냐 클라우드냐" + Analytics 활성 여부는
 * 외부(main.ts resolveServices)에서 결정한다. GameScene.create()가 registry.get으로 꺼내
 * ModuleBrickSaveSystem/분석에 전달(동기 set이라 create 전 반영). 미주입 키는 GameScene의
 * 기본값(로컬 어댑터/Noop)으로 폴백하므로 테스트에서는 필요한 것만 주입하면 된다.
 */
export interface GameServices {
  saveAdapter?: SaveAdapter;
  /** 설정(BrickSettings)은 로컬 전용 어댑터 — 진행 데이터와 달리 클라우드 동기화하지 않는다(기기별 설정). */
  settingsAdapter?: SaveAdapter;
  analyticsSink?: AnalyticsSink;
  /** 빌드별 기능 플래그(정적 + RemoteConfig 킬스위치 반영) — GameScene이 탭/광고 결선 게이트에 사용. */
  featureFlags?: FeatureFlags;
  /** 디바이스 safe-area inset 어댑터 — GameScene.create가 read()로 레이아웃을 노치/홈인디케이터에 맞춰 압축. */
  safeAreaSource?: SafeAreaSource;
  /**
   * 부팅 시 목표 FPS 캡(해석 완료된 수치) — 고주사율 패널(예 120Hz)에서 RAF가 주사율을 추종해
   * 과부하·발열을 유발하므로 setTimeout 기반 step으로 캡한다. 미지정 시 Phaser 기본(주사율 추종).
   */
  initialTargetFps?: number;
  /**
   * 동적 design 높이(화면 비율 추종 — {@link resolveDesignHeight}) — 길쭉한 기기(Flip6 22:9 등)에서
   * FIT 캔버스가 화면 세로를 채워 하단 레터박스를 제거한다. 미지정 시 DESIGN_HEIGHT(844, 회귀 0).
   * GameScene.create가 `this.scale.height`로 읽어 deriveShellLayout에 전달(여분을 게임뷰가 흡수).
   */
  designHeight?: number;
  /**
   * 계정 삭제(resetSave) 시 호출 — Firebase 익명 유저를 Auth에서 제거(orphan 방지). 클라우드 활성
   * (Firebase 결선)일 때만 main.ts가 주입한다. fire-and-forget(내부 timeout)이라 restart를 막지 않는다.
   * GameScene이 firebase SDK를 직접 import하지 않도록 콜백으로만 결선(결합도↓·테스트 용이).
   */
  onDeleteCloudAccount?: () => void;
  /**
   * 계정 연동 Provider(익명→Google) — 클라우드 활성(Firebase 익명 인증 성공)일 때만 main.ts가 주입.
   * 미주입(로컬단독)이면 설정 모달의 연동 버튼이 숨겨진다. GameScene이 registry로 GameBoot에 노출.
   */
  accountLink?: AccountLinkProvider;
  /**
   * 문의하기(고객 지원) 공유 Provider — native share sheet(파일 첨부) / 웹 mailto. 항상 주입(웹 포함).
   * 설정 모달 문의 버튼이 GameBoot.supportShare()로 사용. 미주입 테스트는 GameScene이 noop 폴백.
   */
  supportShare?: SupportShareProvider;
  /**
   * 문의 첨부 로그 수집기 — LogCollector.getLog(deviceMeta)를 디바이스 정보까지 조립한 클로저.
   * main.ts가 부팅 시 install한 수집기 + 디바이스 메타로 만든다. GameBoot.collectSupportLog()로 노출.
   */
  collectSupportLog?: () => string;
  /**
   * Crashlytics Port — 네이티브 크래시 수집 + JS 에러 비치명 기록. 네이티브만 실동작, 웹은 Noop.
   * GameScene이 치트 "강제 크래시"(api.cheats.forceCrash) 결선에 사용. 미주입 테스트는 noop 폴백.
   */
  crashlytics?: CrashlyticsPort;
}

/**
 * Phaser.Game 생성 + 서비스 registry 주입 — main.ts bootGame()에서 순수 추출한 부팅 헬퍼.
 * `overrides`로 테스트(headless 스모크)가 type/scene/audio 등을 교체할 수 있다.
 */
export function createGame(
  services: GameServices,
  overrides?: Partial<Phaser.Types.Core.GameConfig>,
): Phaser.Game {
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game-root",
    backgroundColor: PALETTE.appBackground,
    roundPixels: true,
    ...(FORCE_FAST_RENDER_ENABLED
      ? buildFastRenderConfig({ resolution: FORCE_RENDER_RESOLUTION })
      : FORCE_RENDER_RESOLUTION !== undefined
        ? { resolution: FORCE_RENDER_RESOLUTION }
      : {}),
    // 목표 FPS 캡 — 120Hz 패널 발열 완화 (forceSetTimeOut으로 주사율 추종 차단). 미지정 시 Phaser 기본.
    ...(services.initialTargetFps !== undefined
      ? {
          fps: buildFpsConfig(services.initialTargetFps, {
            forceSetTimeOut: !FORCE_RAF_LOOP_ENABLED,
            smoothStep: FORCE_SMOOTH_STEP,
          }),
        }
      : {}),
    scale: {
      mode: Phaser.Scale.FIT,
      // 가로만 중앙 정렬. CENTER_BOTH는 canvas를 #game-root border-box(safe-area padding 포함) 기준
      // 세로 중앙에 두려 margin-top을 추가해 padding과 이중 적용 → 아래로 밀림. 세로 위치는 padding이
      // 잡으므로 CENTER_HORIZONTALLY로 세로 margin을 0으로 둔다.
      autoCenter: Phaser.Scale.CENTER_HORIZONTALLY,
      width: DESIGN_WIDTH,
      // 동적 design 높이 — 길쭉 기기는 화면 비율 추종값(>844)으로 레터박스 제거, 그 외는 844(회귀 0).
      height: services.designHeight ?? DESIGN_HEIGHT,
      // device-preview가 #game-root을 디바이스 px(예 393×852)로 줄여도 Phaser가 parent를 윈도우
      // 크기로 되돌리지 않도록 — false여야 시뮬레이터 프레임 크기에 맞춰 canvas가 fit된다.
      // (네이티브/일반 웹은 #game-root이 이미 100%라 영향 없음)
      expandParent: false,
    },
    physics: {
      default: "arcade",
      arcade: { gravity: { x: 0, y: 0 }, debug: false },
    },
    scene: [BootScene, GameScene],
    ...overrides,
    // 전투 탭은 우클릭 = 모듈 장착 해제. 웹뷰에서 우클릭/길게누르기 시 브라우저 기본 컨텍스트 메뉴가
    // 뜨는 걸 막는다 — boot 시 MouseManager/TouchManager가 game.canvas에 contextmenu preventDefault
    // 리스너를 자동 등록(캔버스 영역에만 적용 → 페이지 나머지·device-preview 프레임은 영향 없음).
    // overrides 이후 배치 — 테스트/환경 overrides가 실수로 무력화하지 못하도록 강제.
    disableContextMenu: true,
  });
  if (services.saveAdapter) game.registry.set("saveAdapter", services.saveAdapter);
  if (services.settingsAdapter) game.registry.set("settingsAdapter", services.settingsAdapter);
  if (services.analyticsSink) game.registry.set("analyticsSink", services.analyticsSink);
  if (services.featureFlags) game.registry.set("featureFlags", services.featureFlags);
  if (services.safeAreaSource) game.registry.set("safeAreaSource", services.safeAreaSource);
  if (services.onDeleteCloudAccount)
    game.registry.set("onDeleteCloudAccount", services.onDeleteCloudAccount);
  if (services.accountLink) game.registry.set("accountLink", services.accountLink);
  if (services.supportShare) game.registry.set("supportShare", services.supportShare);
  if (services.collectSupportLog)
    game.registry.set("collectSupportLog", services.collectSupportLog);
  if (services.crashlytics) game.registry.set("crashlytics", services.crashlytics);
  if (CHEAT_ENABLED && typeof window !== "undefined") {
    window.__moduleBrickGame = game;
  }
  return game;
}
